// AI-CONTEXT: See docs/pitfalls.md#ui-recharts-series-from-first-row
import toJsDate from './toJsDate';

/**
 * @typedef {object} WeeklyProjectCounts
 * @property {Array<Record<string, number|string>>} rows - One row per ISO week,
 *   shaped `{ name: 'dd-mm', [projectName]: count }`. Every row carries a count
 *   for EVERY project (zero-filled) so a Recharts series list is complete
 *   regardless of which row it is derived from.
 * @property {string[]} projects - Project names, sorted by total count descending.
 */

/**
 * Turn hierarchy event records into a weekly, per-project count dataset for a
 * stacked bar chart: when results came in, and from which child project.
 *
 * @param {Array<{ Timestamp: any, _projectName?: string }>} records
 * @param {string} [dedupeKey] - When set, each project's weekly count is the
 *   number of unique `record[dedupeKey]` values (e.g. `'FormID'` to count
 *   respondents); otherwise every record is counted.
 * @returns {WeeklyProjectCounts}
 */
const buildWeeklyProjectCounts = (records, dedupeKey) => {
    if (!Array.isArray(records) || records.length === 0) {
        return { rows: [], projects: [] };
    }

    /** @type {Map<number, { date: Date, counts: Record<string, Set<string>> }>} */
    const buckets = new Map();
    // Each (week, project) collects a Set of unit ids. With `dedupeKey` the unit
    // is `record[dedupeKey]` (so repeat docs from one respondent count once);
    // without it each record gets a unique id, so the count is the record count.
    let recordSeq = 0;

    records.forEach((record) => {
        const jsDate = toJsDate(record?.Timestamp);
        if (!jsDate || Number.isNaN(jsDate.getTime())) return;

        const projName = record?._projectName || 'Onbekend';
        const dedupeValue = dedupeKey ? record?.[dedupeKey] : null;
        const unitId = dedupeValue != null ? `u:${dedupeValue}` : `r:${recordSeq++}`;

        // Snap to the ISO Monday of that week.
        const monday = new Date(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate());
        const weekdayDiff = (monday.getDay() + 6) % 7;
        monday.setDate(monday.getDate() - weekdayDiff);

        const key = monday.getTime();
        let bucket = buckets.get(key);
        if (!bucket) {
            bucket = { date: monday, counts: {} };
            buckets.set(key, bucket);
        }
        if (!bucket.counts[projName]) bucket.counts[projName] = new Set();
        bucket.counts[projName].add(unitId);
    });

    /** @type {Record<string, number>} */
    const projectTotals = {};
    buckets.forEach((bucket) => {
        Object.entries(bucket.counts).forEach(([projName, set]) => {
            projectTotals[projName] = (projectTotals[projName] || 0) + set.size;
        });
    });

    const projects = Object.keys(projectTotals).sort(
        (a, b) => projectTotals[b] - projectTotals[a],
    );

    const rows = Array.from(buckets.values())
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map(({ date, counts }) => {
            const row = {
                name: `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}`,
            };
            projects.forEach((p) => {
                row[p] = counts[p] ? counts[p].size : 0;
            });
            return row;
        });

    return { rows, projects };
};

export default buildWeeklyProjectCounts;
